import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Card } from '../components/ui';
import { BASE } from '../services/api';
import { AD_PRICE_USD_FALLBACK, EXAMPLE_ADS, formatAdPrice } from '../content/exampleAds';
import styles from './Homepage.module.css';

/**
 * General small-business voice-ad landing using the existing voice-clip
 * offer. Gyms, restaurants, and realtors (EXAMPLE_ADS) are example niches,
 * not the product's audience — keep copy niche-agnostic here.
 */
export default function Homepage() {
  const navigate = useNavigate();
  const [sampleId, setSampleId] = useState(EXAMPLE_ADS[0].id);
  const sample = EXAMPLE_ADS.find((ad) => ad.id === sampleId) || EXAMPLE_ADS[0];
  const [priceUsd, setPriceUsd] = useState(AD_PRICE_USD_FALLBACK);
  const priceLabel = formatAdPrice(priceUsd);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await fetch(`${BASE}/api/payments/ad-offer`, { credentials: 'include' });
        const body = await r.json().catch(() => null);
        if (!cancel && r.ok && Number(body?.priceUsd) > 0) setPriceUsd(Number(body.priceUsd));
      } catch {}
    })();
    return () => { cancel = true; };
  }, []);

  const faqSchema = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: `What does ${priceLabel} USD include?`,
          acceptedAnswer: {
            '@type': 'Answer',
            text: `Your first preview is free to listen to. Downloading that preview costs ${priceLabel} USD. After your free preview, one ${priceLabel} USD payment covers one new generation and its MP3 download. There is no separate download charge for that paid generation.`,
          },
        },
        {
          '@type': 'Question',
          name: 'Does Loudest make the video or add music?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'No. You get an AI-generated voiceover as an MP3 audio file. Video, music, and ad placement are not included. Add the voiceover to your own footage using your usual editor, or play it as an in-store announcement.',
          },
        },
        {
          '@type': 'Question',
          name: 'Do I need an account or a subscription?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'You can preview and buy a clip without creating an account. Checkout asks for your email. Each purchase is a one-time payment, not a subscription.',
          },
        },
      ],
    }),
    [priceLabel]
  );

  const primaryCtas = useMemo(
    () => [
      {
        label: 'Make a clip for my business',
        action: () => navigate('/create'),
        variant: 'primary',
      },
      {
        label: 'Hear more examples',
        action: () => navigate('/discover'),
        variant: 'secondary',
      },
    ],
    [navigate]
  );

  return (
    <main className={styles.page}>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <section className={styles.hero}>
        <div className={styles.heroBadge}>AI voiceovers for small businesses</div>

        <h1 className={styles.heroTitle}>
          Give your offer
          <br />
          a voice.
        </h1>

        <p className={styles.heroSubtitle}>
          Running a sale, a new class, a listing, or an open house?
          Turn your script into an AI voiceover. Download the MP3 to add to
          your own video, play as an in-store announcement, or drop into an ad.
        </p>

        <figure className={styles.sample}>
          <figcaption className={styles.sampleCaption}>Hear an example — {sample.title}</figcaption>
          <div className={styles.sampleTabs} role="tablist" aria-label="Example business type">
            {EXAMPLE_ADS.map((ad) => (
              <button
                key={ad.id}
                type="button"
                role="tab"
                aria-selected={ad.id === sampleId}
                className={styles.sampleTab}
                onClick={() => setSampleId(ad.id)}
              >
                {ad.business}
              </button>
            ))}
          </div>
          <audio className={styles.sampleAudio} controls preload="metadata" src={sample.src}>
            <a href={sample.src}>Play the {sample.title} ad</a>
          </audio>
          <p className={styles.sampleNote}>Fictional business and offer. This is a demonstration, not a customer endorsement.</p>
          <details className={styles.transcript}>
            <summary>Read the example script</summary>
            <p>{sample.script}</p>
          </details>
          <p className={styles.priceLine}>First preview free. {priceLabel} USD per downloadable clip.</p>
        </figure>

        <div className={styles.heroActions}>
          {primaryCtas.map((cta) => (
            <Button
              key={cta.label}
              size="lg"
              variant={cta.variant === 'secondary' ? 'secondary' : 'primary'}
              onClick={cta.action}
            >
              {cta.label}
            </Button>
          ))}
        </div>

        <div className={styles.eq} aria-hidden>
          <span /><span /><span /><span /><span /><span /><span /><span />
        </div>

        <div className={styles.statsRow}>
          <Stat value="Free" label="First preview" />
          <Stat value={priceLabel} label="USD per downloadable clip" />
          <Stat value="MP3" label="Audio file" />
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Your offer. Your script. Your voiceover.</h2>
          <p className={styles.sectionSubtitle}>
            No agency call or studio booking. Just write, choose a voice, and listen.
          </p>
        </div>

        <div className={styles.grid}>
          <FeatureCard
            icon="01"
            title="Write your promotion"
            description="Add your real offer, dates, and details. Your script is what the AI voice reads."
          />
          <FeatureCard
            icon="02"
            title="Choose a voice and listen"
            description="Pick an AI voice that fits your business. Your first preview is free to listen to."
          />
          <FeatureCard
            icon="03"
            title="Download your MP3"
            description="Buy the clip you want, then add the voiceover in your usual video editor or play it as an in-store announcement."
          />
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Give your next promotion a clear message</h2>
          <p className={styles.sectionSubtitle}>
            For independent, local businesses with something specific to announce — gyms, restaurants,
            real estate, retail, and more.
          </p>
        </div>

        <div className={styles.grid}>
          <FeatureCard
            title="New-customer offers"
            description="Explain your intro offer, when it ends, and how someone can claim it."
          />
          <FeatureCard
            title="Launches & events"
            description="Introduce a new class, menu, listing, or sale — share the date and where to go."
          />
          <FeatureCard
            title="Limited-time deals"
            description="Announce an open house, open day, or short-window discount with a clear next step."
          />
        </div>
      </section>

      <section className={styles.section} aria-labelledby="faq-title">
        <div className={styles.sectionHeader}>
          <h2 id="faq-title" className={styles.sectionTitle}>Before you make your first clip</h2>
        </div>
        <div className={styles.faqList}>
          <details className={styles.faqItem}>
            <summary>What does {priceLabel} USD include?</summary>
            <p>
              Your first preview is free to listen to. Downloading that preview costs {priceLabel} USD.
              After your free preview, one {priceLabel} USD payment covers one new generation
              and its MP3 download. There is no separate download charge for that paid generation.
            </p>
          </details>
          <details className={styles.faqItem}>
            <summary>Does Loudest make the video or add music?</summary>
            <p>
              No. You get an AI-generated voiceover as an MP3 audio file. Video, music,
              and ad placement are not included. Add the voiceover to your own footage
              using your usual editor, or play it as an in-store announcement.
            </p>
          </details>
          <details className={styles.faqItem}>
            <summary>Do I need an account or a subscription?</summary>
            <p>
              You can preview and buy a clip without creating an account. Checkout asks
              for your email. Each purchase is a one-time payment, not a subscription.
            </p>
          </details>
        </div>
      </section>

      <section className={styles.section} style={{ maxWidth: 820 }}>
        <Card className={styles.ctaCard}>
          <h2 className={styles.sectionTitle}>Your next promotion starts with a script</h2>
          <p className={styles.sectionSubtitle} style={{ marginBottom: 28 }}>
            Write your offer. Pick an AI voice. Try your first preview free,
            then download your clip for {priceLabel} USD.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', position: 'relative', zIndex: 1 }}>
            <Button size="lg" onClick={() => navigate('/create')}>
              Make a clip for my business
            </Button>
          </div>
        </Card>
      </section>
    </main>
  );
}

function Stat({ value, label }) {
  return (
    <div>
      <div className={styles.statValue}>{value}</div>
      <div className={styles.statLabel}>{label}</div>
    </div>
  );
}

function FeatureCard({ icon, title, description, link }) {
  const inner = (
    <Card hover className={styles.featureCard}>
      {icon && <div className={styles.featureIcon}>{icon}</div>}
      <h3 className={styles.featureTitle}>{title}</h3>
      <p className={styles.featureDesc}>{description}</p>
      {link ? (
        <div className={styles.linkCue}>
          Learn more
          <span aria-hidden>→</span>
        </div>
      ) : null}
    </Card>
  );

  if (link) {
    return (
      <Link to={link} style={{ textDecoration: 'none' }}>
        {inner}
      </Link>
    );
  }

  return inner;
}
